import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useLazyGetApiProjectsDeliverableTypesQuery } from '../../../app/athenaApiGen';
import { useProjectPermissions } from '../useProjectPermissions';
import AddDeliverableTypesDialog from './AddDeliverableTypesDialog';

export default function ProjectTypesPanel({ row }) {
  const theme = useTheme();
  const [fetchDeliverableTypes] = useLazyGetApiProjectsDeliverableTypesQuery();
  const [deliverableTypes, setDeliverableTypes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);

  // Same gate used for the kebab Edit Project — associating a deliverable
  // type with a project is effectively a project mutation; if the BE
  // enforces a finer-grained permission we'll surface that as a 403 from
  // the POST (the dialog catches and shows it inline).
  const { canEditProject } = useProjectPermissions(
    row.projectId,
    row.project?.programId,
  );

  // Hoisted fetch so the post-add callback can refetch without
  // duplicating the network shape between the effect and the handler.
  const loadTypes = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchDeliverableTypes({
        projectId: row.projectId,
      }).unwrap();
      setDeliverableTypes(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching deliverable types:', error);
      setDeliverableTypes([]);
    } finally {
      setLoading(false);
    }
  }, [fetchDeliverableTypes, row.projectId]);

  React.useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  // IDs already on this project — passed to the picker so it can filter
  // them out of the catalog (no double-adds).
  const existingTypeIds = React.useMemo(
    () =>
      deliverableTypes
        .map((dt) => dt.deliverableType?.deliverableTypeId)
        .filter(Boolean),
    [deliverableTypes],
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
        Deliverable Types{loading ? '' : ` (${deliverableTypes.length})`}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : deliverableTypes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No deliverable types found for this project.
        </Typography>
      ) : (
        // Bounded-height scroll container so the table doesn't push the
        // footer button off the panel for projects with many types — the
        // button stays anchored beneath the table as a "footer" no matter
        // the row count.
        <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
          <Table
            size="small"
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              '& .MuiTableCell-root': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Number</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Abbreviation</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deliverableTypes.map((dt, index) => (
                <TableRow
                  key={dt.deliverableTypeId || index}
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <TableCell>{dt.deliverableNumber || '-'}</TableCell>
                  <TableCell>
                    {dt.deliverableType?.title || 'Untitled'}
                  </TableCell>
                  <TableCell>
                    {dt.deliverableType?.abbreviation || '-'}
                  </TableCell>
                  <TableCell>
                    {dt.deliverableType?.isDocument ? (
                      <Chip
                        label="Document"
                        size="small"
                        sx={{
                          backgroundColor: '#2196F3',
                          color: 'white',
                          fontSize: '0.7rem',
                        }}
                      />
                    ) : (
                      <Chip
                        label="Form"
                        size="small"
                        sx={{
                          backgroundColor: '#FF9800',
                          color: 'white',
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Sticky-style footer — Add button below the table with a divider
          above it. Sits outside the scrollable table container so it stays
          visible regardless of how many types are in the list. */}
      {canEditProject && (
        <>
          <Divider sx={{ mt: 1.5, mb: 1.5 }} />
          <Box>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              sx={{
                textTransform: 'none',
                backgroundColor: '#0092CB',
                color: '#fff',
                '&:hover': { backgroundColor: '#007AAB' },
              }}
            >
              Add deliverable types
            </Button>
          </Box>
        </>
      )}

      <AddDeliverableTypesDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        projectId={row.projectId}
        programId={row.project?.programId}
        existingTypeIds={existingTypeIds}
        onAdded={loadTypes}
      />
    </Box>
  );
}

ProjectTypesPanel.propTypes = {
  row: PropTypes.shape({
    projectId: PropTypes.string.isRequired,
  }).isRequired,
};
